import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "toggle", "chevron"]

  connect() {
    this.openPanels = {}
    this.animatingPanels = new Set()

    // Restore state from DOM on connect/reconnect
    this.restoreStateFromDOM()
  }

  disconnect() {
    // No global event listeners to remove
  }

  toggle(event) {
    event.stopPropagation()

    const button = event.currentTarget
    const targetSelector = button.dataset.target
    const placement = button.dataset.panelPlacement || 'right'

    if (!targetSelector) return

    const panel = this.findPanelByPlacement(placement)
    if (!panel) return


    if (panel.classList.contains('show') && this.openPanels[panel.id] === targetSelector) {
      this.closeSpecificPanel(panel.id)
    } else {
      this.openPanel(targetSelector, panel.id)
    }
  }


  findPanelByPlacement(placement) {
    // Find panel based on placement class
    const positionClass = placement === 'left' ? 'start-0' : placement === 'bottom' ? 'bottom-0' : 'end-0'
    return this.panelTargets.find(panel => panel.classList.contains(positionClass))
  }

  open(event) {
    if (event) {
      event.preventDefault();

      const button = event.currentTarget;
      const targetSelector = button.dataset.target;
      const placement = button.dataset.panelPlacement || 'right';

      if (!targetSelector) return;

      // Find the panel with the given placement
      const panel = this.findPanelByPlacement(placement);
      if (!panel) return;

      // Always open the panel, regardless of current state
      this.openPanel(targetSelector, panel.id);
    }
  }


  openPanel(targetSelector, panelId) {
    const contentElement = document.querySelector(targetSelector)
    const panel = document.getElementById(panelId)

    if (!contentElement || !panel) return

    // If panel is already open, don't reopen
    if (this.openPanels[panelId] || contentElement.classList.contains('show')) {
      return
    }

    // Mark as animating
    this.animatingPanels.add(panelId)

    // Set state immediately to prevent double-opening
    this.openPanels[panelId] = targetSelector

    contentElement.classList.remove('d-none')
    panel.classList.remove('d-none')

    // Force a reflow to ensure the element is visible before setting up animation
    contentElement.offsetHeight

    // Check for panel-specific animations and behaviors
    this.applyPanelOpenBehavior(contentElement, panel)

    panel.classList.add('show')

    // Reset comments bar to read state when panel opens
    const commentsBar = document.getElementById('comments-toggle-bar')
    if (commentsBar) {
      const barController = this.application.getControllerForElementAndIdentifier(commentsBar, 'comments-bar')
      if (barController) barController.markAsRead()
    }

    // Update chevron icon to show panel is open
    this.updateChevronIcon(true)

    // Persist state to DOM
    this.persistStateToDOM()

    // Remove animating flag after animation completes
    setTimeout(() => {
      this.animatingPanels.delete(panelId)
    }, 350) // Slightly longer than animation duration
  }

  close(event) {
    if (event) {
      event.preventDefault();

      const panelId = event.currentTarget.dataset.panelId;

      if (panelId) {
        this.closeSpecificPanel(panelId);
      } else {
        const panel = event.currentTarget.closest('[data-turbo-panel-target="panel"]');
        if (panel && panel.id) {
          this.closeSpecificPanel(panel.id);
        } else {
          Object.keys(this.openPanels).forEach(id => {
            this.closeSpecificPanel(id);
          });
        }
      }
    } else {
      Object.keys(this.openPanels).forEach(id => {
        this.closeSpecificPanel(id);
      });
    }
  }

  closeSpecificPanel(panelId) {
    const panel = document.getElementById(panelId)
    if (!panel) return

    // If panel is already animating, don't close
    if (this.animatingPanels.has(panelId)) {
      return
    }

    // Clear state immediately to prevent double-closing
    const contentSelector = this.openPanels[panelId]
    delete this.openPanels[panelId]

    // Mark as animating
    this.animatingPanels.add(panelId)

    if (contentSelector) {
      const contentElement = document.querySelector(contentSelector)
      if (contentElement) {
        // Apply panel-specific close behavior
        this.applyPanelCloseBehavior(contentElement, panel, panelId)
      }
    } else {
      panel.classList.remove('show')
      panel.classList.add('d-none')
      this.animatingPanels.delete(panelId)
    }

    // Clear DOM state
    if (panel) {
      delete panel.dataset.panelOpen
      delete panel.dataset.panelTarget
    }

    // Update chevron icon to show panel is closed
    this.updateChevronIcon(false)
  }

  // Generic method to apply panel open behavior based on data attributes
  applyPanelOpenBehavior(contentElement, panel) {
    const animationType = contentElement.dataset.panelAnimation
    const hideToggleSelector = contentElement.dataset.panelHideToggle

    if (animationType === 'slide-up') {
      // Set initial state for bottom slide animation
      contentElement.style.transform = 'translateY(100%)'
      contentElement.style.transition = 'transform 0.3s ease-in-out'

      // Trigger animation on next frame
      requestAnimationFrame(() => {
        contentElement.style.transform = 'translateY(0)'
      })
    }

    // Hide specified toggle element when panel opens
    if (hideToggleSelector) {
      const toggleToHide = document.querySelector(hideToggleSelector)
      if (toggleToHide) {
        toggleToHide.style.display = 'none'
      }
    }
  }

  // Generic method to apply panel close behavior based on data attributes
  applyPanelCloseBehavior(contentElement, panel, panelId) {
    const animationType = contentElement.dataset.panelAnimation
    const animationDuration = parseInt(contentElement.dataset.panelAnimationDuration) || 300

    if (animationType === 'slide-up') {
      // Set initial state if not already set
      if (!contentElement.style.transition) {
        contentElement.style.transition = 'transform 0.3s ease-in-out'
      }

      // Animate slide down
      contentElement.style.transform = 'translateY(100%)'

      // Show the hidden toggle again if specified
      const hideToggleSelector = contentElement.dataset.panelHideToggle
      if (hideToggleSelector) {
        const toggleToShow = document.querySelector(hideToggleSelector)
        if (toggleToShow) {
          toggleToShow.style.display = ''
        }
      }

      // Wait for animation to complete before hiding
      setTimeout(() => {
        contentElement.classList.add('d-none')
        panel.classList.remove('show')
        panel.classList.add('d-none')
        // Reset transform to initial position for next time
        contentElement.style.transform = 'translateY(100%)'
        // Remove animating flag
        this.animatingPanels.delete(panelId)
      }, animationDuration)
    } else {
      // No animation - immediate hide
      const hideToggleSelector = contentElement.dataset.panelHideToggle
      if (hideToggleSelector) {
        const toggleToShow = document.querySelector(hideToggleSelector)
        if (toggleToShow) {
          toggleToShow.style.display = ''
        }
      }

      contentElement.classList.add('d-none')
      panel.classList.remove('show')
      panel.classList.add('d-none')
      // Remove animating flag
      this.animatingPanels.delete(panelId)
    }
  }

  // Update chevron icon direction based on panel state
  updateChevronIcon(isOpen) {
    if (this.hasChevronTarget) {
      if (isOpen) {
        this.chevronTarget.classList.remove('fa-chevron-up')
        this.chevronTarget.classList.add('fa-chevron-down')
      } else {
        this.chevronTarget.classList.remove('fa-chevron-down')
        this.chevronTarget.classList.add('fa-chevron-up')
      }
    }
  }

  // Store panel state in DOM to persist across turbo-frame reloads
  persistStateToDOM() {
    Object.keys(this.openPanels).forEach(panelId => {
      const panel = document.getElementById(panelId)
      if (panel) {
        panel.dataset.panelOpen = 'true'
        panel.dataset.panelTarget = this.openPanels[panelId]
      }
    })
  }

  // Restore panel state from DOM after connect/reconnect
  restoreStateFromDOM() {
    this.panelTargets.forEach(panel => {
      if (panel.dataset.panelOpen === 'true' && panel.dataset.panelTarget) {
        this.openPanels[panel.id] = panel.dataset.panelTarget
      }
    })
  }

}
